import {
  BookOpen,
  Eye,
  Handshake,
  Lightbulb,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from "lucide-react";

const ICON_STROKE = 1.5;

const VALUE_ICONS = {
  integrity: ShieldCheck,
  excellence: Sparkles,
  inclusion: Users,
  innovation: Lightbulb,
  service: Handshake,
};

function valueIcon(name) {
  return VALUE_ICONS[name.toLowerCase()] ?? Scale;
}

export function AboutVisionSection({ uni }) {
  const values = uni.coreValues?.items ?? [];

  return (
    <>
      <section
        id="about"
        className="scroll-mt-28 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-soft sm:p-10"
        aria-labelledby="about-heading"
      >
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-100 pb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-maroon-700">
              Who we are
            </p>
            <h1
              id="about-heading"
              className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
            >
              {uni.about.title}
            </h1>
          </div>
          <BookOpen
            className="hidden h-10 w-10 text-maroon-200 sm:block"
            strokeWidth={ICON_STROKE}
            aria-hidden
          />
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {uni.about.paragraphs.map((p, i) => (
            <p key={i} className="text-base leading-relaxed text-slate-600">
              {p}
            </p>
          ))}
        </div>
      </section>

      <section
        className="scroll-mt-28 mt-10 grid gap-6 lg:grid-cols-2 sm:mt-12"
        aria-labelledby="vision-heading"
      >
        <article className="rounded-2xl border border-maroon-100 bg-gradient-to-br from-white to-maroon-50/80 p-6 shadow-soft sm:p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-maroon-900 text-white shadow-md shadow-maroon-900/20">
              <Eye className="h-6 w-6" strokeWidth={ICON_STROKE} aria-hidden />
            </span>
            <h2
              id="vision-heading"
              className="text-xl font-bold text-maroon-900 sm:text-2xl"
            >
              {uni.vision.title}
            </h2>
          </div>
          <p className="mt-5 text-base leading-relaxed text-slate-700">{uni.vision.text}</p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft sm:p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink-900 text-white shadow-md">
              <Target className="h-6 w-6" strokeWidth={ICON_STROKE} aria-hidden />
            </span>
            <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">{uni.mission.title}</h2>
          </div>
          <p className="mt-5 text-base leading-relaxed text-slate-600">{uni.mission.text}</p>
        </article>
      </section>

      {values.length > 0 ? (
        <section
          id="values"
          className="scroll-mt-28 mt-10 sm:mt-12"
          aria-labelledby="values-heading"
        >
          <h2
            id="values-heading"
            className="flex items-center gap-2 border-l-4 border-maroon-700 pl-4 text-lg font-bold text-slate-900"
          >
            <Sparkles className="h-5 w-5 text-maroon-700 opacity-90" strokeWidth={ICON_STROKE} aria-hidden />
            {uni.coreValues.title}
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {values.map((v) => {
              const Icon = valueIcon(v.name);
              return (
                <article
                  key={v.name}
                  className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_2px_16px_rgba(15,23,42,0.06)] transition hover:border-maroon-200 hover:shadow-[0_4px_24px_rgba(15,23,42,0.08)]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-maroon-50 text-maroon-800">
                    <Icon className="h-5 w-5" strokeWidth={ICON_STROKE} aria-hidden />
                  </span>
                  <p className="mt-4 text-base font-bold text-slate-900">{v.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.description}</p>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}

/** About, vision, and mission — single page linked from primary navigation. */
export function AboutVisionPage({ uni }) {
  return (
    <main id="main" className="flex-1">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <AboutVisionSection uni={uni} />
      </div>
    </main>
  );
}
